import { formatCompactDollars, formatPersonDateForDisplay } from "@/shared/utils/formatters";
import { parseAddress, toStateAbbreviation } from "@/shared/utils/addressUtils";
import {
  BRAND_GREEN,
  TEXT_PRIMARY,
  TEXT_SECONDARY,
  BORDER_COLOR,
  loadLogoAsDataUrl,
} from "@/shared/utils/pdfBranding";

import type { RGBTuple } from "@/shared/utils/pdfBranding";
import type { LawyerEstateRow } from "@/shared/types/lawyerDashboard";

const ROW_BG_ALT: RGBTuple = [249, 247, 247];
const WARNING_COLOR: RGBTuple = [166, 95, 0];

const MARGIN = 16;
const ROW_HEIGHT = 14;
const HEADER_ROW_HEIGHT = 10;

function formatLocation(location: string | null): string {
  if (!location) return "—";
  const parsed = parseAddress(location);
  const abbr = toStateAbbreviation(parsed.state);
  if (parsed.city && abbr) return `${parsed.city}, ${abbr}`;
  return abbr || location;
}

interface Column {
  header: string;
  width: number;
  getValue: (row: LawyerEstateRow) => string;
  getSubValue?: (row: LawyerEstateRow) => string;
  getColor?: (row: LawyerEstateRow) => RGBTuple;
}

export async function generateDashboardPdf(rows: LawyerEstateRow[]): Promise<void> {
  const pdfWindow = window.open("", "_blank");

  const { default: jsPDF } = await import("jspdf");
  const doc = new jsPDF({ orientation: "landscape", unit: "mm", format: "a4" });
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const contentWidth = pageWidth - 2 * MARGIN;
  let y = MARGIN;

  const cols: Column[] = [
    {
      header: "Client",
      width: 0.16,
      getValue: (r) => r.clientName ?? "—",
      getSubValue: (r) => r.clientRole ?? "",
    },
    {
      header: "Decedent",
      width: 0.16,
      getValue: (r) => r.decedentName ?? "—",
      getSubValue: (r) =>
        r.dateOfDeath
          ? `DOD: ${formatPersonDateForDisplay(r.dateOfDeath, { month: "2-digit", day: "2-digit", year: "numeric" })}`
          : "",
    },
    {
      header: "Proceeding",
      width: 0.14,
      getValue: (r) => r.proceedingType,
      getSubValue: (r) => r.authoritySuffix,
    },
    {
      header: "Highlight",
      width: 0.16,
      getValue: (r) => r.highlight?.label ?? "—",
      getSubValue: (r) => r.highlight?.timeAgo ?? "",
      getColor: (r) => (r.highlight?.category === "action" ? WARNING_COLOR : TEXT_PRIMARY),
    },
    {
      header: "Next Step",
      width: 0.14,
      getValue: (r) => r.highlight?.nextStep ?? "—",
    },
    {
      header: "Inventory",
      width: 0.12,
      getValue: (r) =>
        r.assetCount > 0 || r.liabilityCount > 0 ? formatCompactDollars(r.inventoryTotal) : "—",
      getSubValue: (r) => {
        if (r.assetCount === 0 && r.liabilityCount === 0) return "";
        return `${r.assetCount} assets, ${r.liabilityCount} liabilities`;
      },
    },
    {
      header: "Location",
      width: 0.12,
      getValue: (r) => formatLocation(r.location),
    },
  ];

  const colWidths = cols.map((c) => c.width * contentWidth);
  const startX = MARGIN;

  const checkPage = (needed: number) => {
    if (y + needed > pageHeight - 20) {
      doc.addPage();
      y = MARGIN;
      return true;
    }
    return false;
  };

  const logoDataUrl = await loadLogoAsDataUrl();
  if (logoDataUrl) {
    const logoH = 7;
    const logoW = logoH * (1701 / 441);
    doc.addImage(logoDataUrl, "PNG", MARGIN, y, logoW, logoH);
  }

  const today = new Date().toLocaleDateString("en-US", {
    month: "long",
    day: "numeric",
    year: "numeric",
  });
  doc.setFontSize(8);
  doc.setTextColor(...TEXT_SECONDARY);
  doc.text(today, pageWidth - MARGIN, y + 5, { align: "right" });
  y += 12;

  doc.setDrawColor(...BORDER_COLOR);
  doc.setLineWidth(0.4);
  doc.line(MARGIN, y, pageWidth - MARGIN, y);
  y += 8;

  doc.setFontSize(18);
  doc.setFont("helvetica", "bold");
  doc.setTextColor(...TEXT_PRIMARY);
  doc.text("Estate Matters Summary", MARGIN, y);
  y += 6;

  doc.setFontSize(9);
  doc.setFont("helvetica", "normal");
  doc.setTextColor(...TEXT_SECONDARY);

  const totalInventory = rows.reduce((sum, r) => sum + r.inventoryTotal, 0);
  const actionCount = rows.filter((r) => r.highlight?.category === "action").length;
  doc.text(
    `${rows.length} open ${rows.length === 1 ? "matter" : "matters"} · ${formatCompactDollars(totalInventory)} in administration · ${actionCount} need action`,
    MARGIN,
    y + 4,
  );
  y += 12;

  doc.setFillColor(250, 250, 249);
  doc.setDrawColor(...BORDER_COLOR);
  doc.roundedRect(startX, y, contentWidth, 20, 2, 2, "FD");

  const summaryColWidth = contentWidth / 3;
  const summaryItems = [
    { label: "Open Matters", value: String(rows.length) },
    { label: "In Administration", value: formatCompactDollars(totalInventory) },
    { label: "Need Action", value: String(actionCount) },
  ];
  summaryItems.forEach((item, i) => {
    const colX = MARGIN + summaryColWidth * i + 8;
    doc.setFontSize(7);
    doc.setFont("helvetica", "normal");
    doc.setTextColor(...TEXT_SECONDARY);
    doc.text(item.label, colX, y + 7);
    doc.setFontSize(12);
    doc.setFont("helvetica", "bold");
    doc.setTextColor(...TEXT_PRIMARY);
    doc.text(item.value, colX, y + 15);
  });
  y += 26;

  const drawHeaderRow = () => {
    doc.setFillColor(...BRAND_GREEN);
    doc.rect(startX, y, contentWidth, HEADER_ROW_HEIGHT, "F");

    doc.setFontSize(7);
    doc.setFont("helvetica", "bold");
    doc.setTextColor(255, 255, 255);

    let x = startX;
    for (let i = 0; i < cols.length; i++) {
      doc.text(cols[i].header, x + 3, y + 6.5);
      x += colWidths[i];
    }
    y += HEADER_ROW_HEIGHT;
  };

  drawHeaderRow();

  doc.setFont("helvetica", "normal");

  for (let rowIdx = 0; rowIdx < rows.length; rowIdx++) {
    const isNewPage = checkPage(ROW_HEIGHT);
    if (isNewPage) drawHeaderRow();

    const row = rows[rowIdx];

    if (rowIdx % 2 === 1) {
      doc.setFillColor(...ROW_BG_ALT);
      doc.rect(startX, y, contentWidth, ROW_HEIGHT, "F");
    }

    doc.setDrawColor(...BORDER_COLOR);
    doc.setLineWidth(0.2);
    doc.line(startX, y + ROW_HEIGHT, startX + contentWidth, y + ROW_HEIGHT);

    let x = startX;
    for (let i = 0; i < cols.length; i++) {
      const col = cols[i];
      const cellW = colWidths[i] - 6;
      const value = col.getValue(row);
      const subValue = col.getSubValue?.(row) ?? "";
      const color = col.getColor?.(row) ?? TEXT_PRIMARY;

      doc.setFontSize(8);
      doc.setFont("helvetica", "bold");
      doc.setTextColor(...color);
      const mainText = doc.splitTextToSize(value, cellW);
      doc.text(mainText[0] ?? "", x + 3, y + 5.5);

      if (subValue) {
        doc.setFontSize(6.5);
        doc.setFont("helvetica", "normal");
        doc.setTextColor(...TEXT_SECONDARY);
        const subText = doc.splitTextToSize(subValue, cellW);
        doc.text(subText[0] ?? "", x + 3, y + 10);
      }

      x += colWidths[i];
    }

    y += ROW_HEIGHT;
  }

  const pageCount = doc.getNumberOfPages();
  for (let i = 1; i <= pageCount; i++) {
    doc.setPage(i);

    doc.setDrawColor(...BORDER_COLOR);
    doc.setLineWidth(0.3);
    doc.line(MARGIN, pageHeight - 14, pageWidth - MARGIN, pageHeight - 14);

    doc.setFontSize(7);
    doc.setFont("helvetica", "normal");
    doc.setTextColor(...BRAND_GREEN);
    doc.text("Elayne for Attorneys", MARGIN, pageHeight - 8);

    doc.setTextColor(...TEXT_SECONDARY);
    doc.text(`Page ${i} of ${pageCount}`, pageWidth - MARGIN, pageHeight - 8, {
      align: "right",
    });
  }

  const blobUrl = doc.output("bloburl").toString();
  if (pdfWindow) {
    pdfWindow.location.href = blobUrl;
  } else {
    window.open(blobUrl, "_blank");
  }
}
